import { useMutation, useQueryClient } from '@tanstack/react-query'
import Activity from '../../model/Activity'
import ActivityLog from '../../model/ActivityLog'
import Badge from '../../model/Badge'
import { dataSource } from '../../service/domain/_service.config'
import { notificationService } from '../../service/domain/NotificationService'
import ListUtil from '../../util/ListUtil'
import { logError } from '../../util/errorLogger'
import ActivityLogic from '../../logic/ActivityLogic'
import { isActivityUnlocked } from '../../logic/activityAccess'
import { crossedCheckpoints } from '../../logic/checkpointUtils'
import { capRmsAtNextGate, rmsUntilNextGate } from '../../logic/gateUtils'
import { useCheckBadges } from '../badges/useCheckBadges'
import { useCheckTeamBadges } from '../badges/useCheckTeamBadges'
import { useChallengeAccess } from '../membership/useChallengeAccess'
import { useCheckpoints } from '../checkpoint/useCheckpoints'
import { useActiveGate } from '../gate/useActiveGate'
import { useGates } from '../gate/useGates'
import { useRestDays } from '../restDays/useRestDays'
import { useTeam } from '../team/useTeam'
import { useUser } from '../user/useUser'
import { ACTIVITY_LOGS_QUERY_KEY, useActivityLogs } from './useActivityLogs'

export interface LogActivityResult {
  activityLog: ActivityLog
  earnedBadges: Badge[]
  earnedTeamBadges: Badge[]
}

export function useLogActivity() {
  const queryClient = useQueryClient()
  const { data: user } = useUser()
  const { teamID, challengeId } = useChallengeAccess()
  const { data: team } = useTeam(teamID)
  const { mutateAsync: checkBadges } = useCheckBadges()
  const { isActive } = useRestDays()
  const { mutateAsync: checkTeamBadges } = useCheckTeamBadges()
  const { data: gates, isLoading: gatesLoading } = useGates()
  const { data: checkpoints, isLoading: checkpointsLoading } = useCheckpoints()
  const { data: activityLogs, isLoading: logsLoading } = useActivityLogs()
  const { data: activeGate, isLoading: gateLoading } = useActiveGate()

  return useMutation({
    mutationFn: async ({
      activity,
      value,
    }: {
      activity: Activity
      value: number
    }): Promise<LogActivityResult> => {
      if (!user) throw new Error('User not found')
      if (!teamID) throw new Error('User has no team')
      if (!challengeId) throw new Error('No active challenge')
      if (!team) throw new Error('Team not found')
      if (isActive) throw new Error('You cannot log activities during a rest day')
      if (gatesLoading || gateLoading || logsLoading || checkpointsLoading) {
        throw new Error('Activities are still loading')
      }
      if (!activityLogs || !checkpoints || !gates) throw new Error('Activities are still loading')
      if (activeGate) {
        throw new Error('Complete the active gate before logging')
      }
      if (
        !isActivityUnlocked(
          activity,
          gates ?? [],
          ActivityLogic.getTotalProgress(activityLogs ?? []),
        )
      ) {
        throw new Error('This activity is locked')
      }

      const progressBefore = ActivityLogic.getTotalProgress(activityLogs)
      const proposedRms = value * activity.unitConversion
      const { creditedRms, cutRms } = capRmsAtNextGate(
        proposedRms,
        rmsUntilNextGate(gates ?? [], progressBefore),
      )
      const activityLog = new ActivityLog({
        userID: user.id,
        teamID,
        activityID: activity.id,
        amount: creditedRms,
        value,
        challengeID: challengeId,
      })

      const logsKey = ACTIVITY_LOGS_QUERY_KEY(teamID)
      queryClient.setQueryData(logsKey, (old: ActivityLog[] | undefined) =>
        ListUtil.put(old ?? [], activityLog, (log) => log.id === activityLog.id),
      )

      try {
        await dataSource.saveActivityLog(activityLog)
      } catch (error) {
        queryClient.setQueryData(logsKey, (old: ActivityLog[] | undefined) =>
          ListUtil.remove(old ?? [], (log) => log.id === activityLog.id),
        )
        throw error
      }

      queryClient.invalidateQueries({ queryKey: logsKey })
      queryClient.invalidateQueries({ queryKey: ['leaderboard'] })

      try {
        const notifiedValue =
          cutRms > 0 && activity.unitConversion
            ? Math.round((creditedRms / activity.unitConversion) * 1000) / 1000
            : value
        await notificationService.sendTeamActivityNotification(
          activityLog,
          team.members,
          user,
          activity,
          notifiedValue,
        )
      } catch (error) {
        void logError(error, {
          hook: 'useLogActivity',
          action: 'sendTeamActivityNotification',
          activityLogId: activityLog.id,
        })
      }

      const progressAfter = progressBefore + activityLog.amount
      for (const checkpoint of crossedCheckpoints(checkpoints, progressBefore, progressAfter)) {
        const gate = (gates ?? []).find((candidate) => candidate.checkpointID === checkpoint.id)
        try {
          if (gate) {
            await notificationService.sendGateReachedNotification(
              gate.displayTitle(),
              team.members,
              user.id,
              teamID,
              gate.id,
            )
          } else {
            await notificationService.sendCheckpointReachedNotification(
              checkpoint.title,
              team.members,
              user.id,
              teamID,
              checkpoint.id,
            )
          }
        } catch (error) {
          void logError(error, {
            hook: 'useLogActivity',
            action: gate ? 'sendGateReachedNotification' : 'sendCheckpointReachedNotification',
            checkpointId: checkpoint.id,
          })
        }
      }

      const cachedLogs = queryClient.getQueryData<ActivityLog[]>(logsKey)
      const allTeamLogs: ActivityLog[] =
        cachedLogs ?? (await dataSource.getActivityLogs(teamID))
      const userActivityLogs = allTeamLogs.filter((log) => log.userID === user.id)

      const earnedBadges = await checkBadges({
        userID: user.id,
        activityLog,
        activityLogs: userActivityLogs,
      })

      const earnedTeamBadges = await checkTeamBadges({
        teamID,
        earnedByUserID: user.id,
        teamActivityLogs: allTeamLogs,
      })

      const sendPushPerBadge = earnedTeamBadges.length <= 1
      for (const badge of earnedTeamBadges) {
        try {
          await notificationService.sendTeamBadgeNotification(
            badge.name,
            team.members,
            user.id,
            teamID,
            badge.id,
            sendPushPerBadge,
          )
        } catch (err) {
          void logError(err, {
            hook: 'useLogActivity',
            action: 'sendTeamBadgeNotification',
            badgeId: badge.id,
          })
        }
      }

      if (earnedTeamBadges.length > 1) {
        try {
          await notificationService.sendTeamBadgesBatchedPush(
            earnedTeamBadges.length,
            team.members,
            user.id,
            teamID,
          )
        } catch (err) {
          void logError(err, {
            hook: 'useLogActivity',
            action: 'sendTeamBadgesBatchedPush',
            count: earnedTeamBadges.length,
          })
        }
      }

      return { activityLog, earnedBadges, earnedTeamBadges }
    },
  })
}
